import React, { useEffect } from 'react'
import map from 'lodash/map'
import { Droppable, Draggable } from '@hello-pangea/dnd'
import Task from './Task'
import Icon from '../../shared/icon'

const Dropzone = ({
  number, stepName, title, description, currentDraggable, hoveredDraggable, updateTaskField,
  orderedTasks, removeTask, removeProductFromTask, ungroupTask, cookingModes, unloadModes,
  updateTaskProductParameter, updateTaskParameter, updateProductConfigurable, updateProductCritical,
  insertUnloadBoiler, readOnly = false
}) => {
  const dropzoneFocusClass = () => {
    if (!currentDraggable) return '' // Not dragging anything

    if (stepName === 'non_cooked_base_tasks' && currentDraggable.operation === 'add_product') return 'enabled'
    if (stepName === 'boiler_tasks' && currentDraggable.operation !== 'add_batch_recipe') return 'enabled' // Can drop anything into boiler tasks
    if (stepName === 'solid_topping_tasks' && (currentDraggable.operation === 'add_product' || currentDraggable.operation === 'add_batch_recipe')) return 'enabled'
    if (stepName === 'liquid_topping_tasks' && currentDraggable.operation === 'add_sauce') return 'enabled'

    return 'disabled'
  }

  const needToAddUnload = () => {
    const isBoilerZone = stepName === 'boiler_tasks'
    const hasAnyBoilerTasks = !!orderedTasks.length
    const unloadNotLast = orderedTasks[orderedTasks.length - 1]?.operation !== 'unload_boiler'

    return isBoilerZone && hasAnyBoilerTasks && unloadNotLast
  }

  useEffect(() => {
    if (!readOnly && needToAddUnload()) {
      insertUnloadBoiler()
    }
  }, [orderedTasks, stepName])

  const isDropDisabled = (dropzoneFocusClass() === 'disabled')

  const isDraggingProduct = currentDraggable && currentDraggable.operation === 'add_product'
  const isNotHoveringOperation = !hoveredDraggable || hoveredDraggable.operation === 'add_product'

  const isCombineEnabled = !!((stepName === 'boiler_tasks') && isDraggingProduct && isNotHoveringOperation)

  return (
    <div className={`recipe-tasks-dropzone ${dropzoneFocusClass()}`}>
      <p className='dropzone-title'>
        <span>Step {number}: </span>
        <b>{title}</b>
      </p>

      {orderedTasks.length === 0 &&
        <div className='drag-and-drop-hint'>
          {description}
        </div>
      }

      <Droppable
        droppableId={stepName}
        isDropDisabled={isDropDisabled}
        isCombineEnabled={isCombineEnabled}
      >
        {(provided, snapshot) =>
          <div
            className='droppable'
            data-step-name={stepName}
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {map(orderedTasks, (task, index) =>
              <Draggable
                key={index}
                draggableId={JSON.stringify({ ...task, stepName: stepName })}
                index={index}
                isDragDisabled={readOnly}
              >
                {(provided, snapshot) =>
                  <Task
                    {...task}
                    key={index}
                    passedRef={provided.innerRef}
                    draggableProps={provided.draggableProps}
                    dragHandleProps={provided.dragHandleProps}
                    ungroupTask={ungroupTask}
                    removeTask={() => removeTask(stepName, index)}
                    removeProductFromTask={(productIndex) => removeProductFromTask(stepName, index, productIndex)}
                    isDragging={snapshot.isDragging}
                    combineTargetFor={snapshot.combineTargetFor}
                    taskIndex={index}
                    cookingModes={cookingModes}
                    unloadModes={unloadModes}
                    updateTaskProductParameter={updateTaskProductParameter}
                    updateTaskField={updateTaskField}
                    updateTaskParameter={updateTaskParameter}
                    stepName={stepName}
                    updateProductConfigurable={updateProductConfigurable}
                    updateProductCritical={updateProductCritical}
                    groupNumber={task.group_number}
                    readOnly={readOnly}
                  />
                }
              </Draggable>
            )}
            {provided.placeholder}
          </div>
        }
      </Droppable>
      {isDropDisabled &&
        <div className='disabled-overlay'>
          This product does not meet <br/>
          criteria for this step
        </div>
      }
    </div>
  )
}

export default Dropzone
